import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { match, useHistory, useRouteMatch } from 'react-router-dom'
import { Divider } from '@fluentui/react-components'
import { useDispatch } from 'react-redux'

import Loading from '../../../components/Loading'
import useEntity from '../../../hooks/useEntity'
import { AppAction, IBackgroundLocationState } from '../../../config/appConfig'
import { MenuItemKey } from '../../../components/MainMenu'
import { generatePath, Route, Switch } from 'react-router'
import { usePageStyles } from '../File/ViewFile'
import ViewCRMHeader from './ViewCRMHeader'
import ViewCRMTabs, { CRMTabKey } from './ViewCRMTabs'
import CRMFileOverview from './Tabs/CRMFileOverview'
import CRMDocumentOverview from './Tabs/CRMDocumentOverview'
import CRMFinanceTab from './Tabs/CRMFinanceTab'
import useModules from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'
import { MutationRouteMatchProps } from '../../../pages/BaseMutableOverview/BaseMutableOverview'
import { BasePartyDto, NaturalPersonDto } from '../../../config/partyConfig'
import Tasks from '../../../pages/Tasks'
import EditNaturalPerson from '../../../pages/Party/EditNaturalPerson'
import EditLegalPerson from '../../../pages/Party/EditLegalPerson'
import Workflows from '../../../pages/Workflows'

export const crmRoutePrefix = '/crm/:crmRelationId'

export type CRMRouteMatchProps = { crmRelationId: string; tab?: CRMTabKey } & MutationRouteMatchProps

export type ViewCRMProps = {
    crmRelationId: string
    routeMatch: match<CRMRouteMatchProps>

    crmRelation: BasePartyDto
    resetCRMRelation: () => void

    contacts: NaturalPersonDto[]
    resetContacts: () => void
}

export default function ViewCRM(): ReactElement {
    const routeMatch = useRouteMatch<CRMRouteMatchProps>()
    const crmRelationId = routeMatch.params?.crmRelationId
    const baseUrl = generatePath(crmRoutePrefix, { crmRelationId: crmRelationId })

    const dispatch = useDispatch()
    const history = useHistory<IBackgroundLocationState>()
    const modules = useModules()
    const pageStyles = usePageStyles()

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.CRM] })
    }, [])

    const [crmRelation, , resetCRMRelation] = useEntity<BasePartyDto>(() => ({ url: `/party/${crmRelationId}/details` }), crmRelationId || false)
    const [contacts, , resetContacts] = useEntity<NaturalPersonDto[]>(() => ({ url: `/crm/${crmRelationId}/contacts` }), crmRelationId || false)

    useEffect(() => {
        console.log('crmRelationId', history.location)
        // resetCRMRelation()
        // resetContacts()
    }, [history.location])

    const componentProps = useMemo<ViewCRMProps>(
        () => ({
            crmRelationId,
            routeMatch,
            crmRelation: crmRelation.data,
            resetCRMRelation,
            contacts: contacts.data,
            resetContacts,
        }),
        [routeMatch, crmRelation.loaded, contacts.loaded],
    )

    if (!crmRelation.loaded) {
        return <Loading />
    }

    return (
        <div className={pageStyles.page}>
            <ViewCRMHeader {...componentProps} />
            <ViewCRMTabs {...componentProps} />
            <Divider />
            <Switch>
                <Route path={`${crmRoutePrefix}/natural/:id/edit`} component={EditNaturalPerson} />
                <Route path={`${crmRoutePrefix}/legal/:id/edit`} component={EditLegalPerson} />

                <Route path={`${crmRoutePrefix}/${CRMTabKey.File}`} render={() => <CRMFileOverview baseUrl={baseUrl} {...componentProps} />} />

                <Route path={`${crmRoutePrefix}/${CRMTabKey.Document}`} render={() => <CRMDocumentOverview {...componentProps} />} />

                {modules.has(ModuleType.Tasks) && (
                    <Route
                        path={`${crmRoutePrefix}/${CRMTabKey.Task}/:action?`}
                        render={() => <Tasks routePrefix={crmRoutePrefix} currentCRMRelation={crmRelation.data} />}
                    />
                )}
                {modules.has(ModuleType.Tasks) && (
                    <Route
                        path={`${crmRoutePrefix}/${CRMTabKey.Workflow}/:action?`}
                        render={() => <Workflows routePrefix={crmRoutePrefix} currentCRMRelation={crmRelation.data} />}
                    />
                )}
                {modules.has(ModuleType.Invoices) && (
                    <Route path={`${crmRoutePrefix}/${CRMTabKey.Finance}`} render={() => <CRMFinanceTab {...componentProps} />} />
                )}
            </Switch>
        </div>
    )
}
